import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, Trash2 } from 'lucide-react'
import { applicationsApi } from '../../services/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import { formatDateTime } from '../../utils/date'
import { useMarkNotificationsRead } from '../../hooks/useMarkNotificationsRead'
import { useAuth } from '../../context/AuthContext'
import { formatCampusLabel } from '../../utils/campuses'

const STATUSES = ['new', 'reviewed', 'contacted', 'accepted', 'declined']

export default function AdminApplications() {
  const { t } = useTranslation()
  const { hasModuleAccess } = useAuth()
  const canDelete = hasModuleAccess('forms_applications', 'can_delete')
  const canEdit = hasModuleAccess('forms_applications', 'can_edit')
  useMarkNotificationsRead('forms_applications')

  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const load = (pageNum = page) => {
    setLoading(true)
    applicationsApi
      .getAllAdmin({ page: pageNum, per_page: 15 })
      .then(({ data }) => {
        setItems(data.data || [])
        setMeta(data.meta || null)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(page)
  }, [page])

  const updateStatus = async (id, status) => {
    await applicationsApi.updateStatus(id, { status })
    load(page)
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.applications.confirmDelete'))) return

    await applicationsApi.delete(id)
    load(page)
  }

  return (
    <div>
      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <>
          <Card padding={false} className="overflow-x-auto">
            <table className="min-w-full text-start text-sm">
              <thead className="border-b border-border bg-primary/5">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold text-primary">{t('admin.applications.student')}</th>
                  <th className="px-4 py-3 text-start font-semibold text-primary">{t('admin.applications.program')}</th>
                  <th className="px-4 py-3 text-start font-semibold text-primary">{t('admin.applications.branches')}</th>
                  <th className="px-4 py-3 text-start font-semibold text-primary">{t('admin.table.status')}</th>
                  <th className="px-4 py-3 text-start font-semibold text-primary">{t('admin.applications.date')}</th>
                  <th className="px-4 py-3 text-start font-semibold text-primary">{t('admin.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text/50">
                      {t('admin.table.noData')}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="border-b border-border/60 align-top last:border-0">
                      <td className="px-4 py-3 font-medium text-text">{item.student_name}</td>
                      <td className="px-4 py-3">{item.program || '—'}</td>
                      <td className="px-4 py-3">{formatCampusLabel(item.campus, t)}</td>
                      <td className="px-4 py-3">
                        {canEdit ? (
                          <select
                            value={item.status}
                            onChange={(event) => updateStatus(item.id, event.target.value)}
                            className="rounded-lg border border-border px-2 py-1 text-xs capitalize"
                          >
                            {STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="capitalize text-text/80">{item.status}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-text/60">
                        {item.created_at ? formatDateTime(item.created_at) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-start gap-1">
                          <Link
                            to={`/cp-login/forms/applications/${item.id}`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/5"
                            title={t('admin.applications.view')}
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          {canDelete ? (
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 transition-colors hover:bg-red-50"
                              title={t('buttons.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>

          {meta && meta.last_page > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outlineDark" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('admin.pagination.prev')}
              </Button>
              <span className="text-sm text-text/60">
                {page} / {meta.last_page}
              </span>
              <Button
                variant="outlineDark"
                size="sm"
                disabled={page >= meta.last_page}
                onClick={() => setPage(page + 1)}
              >
                {t('admin.pagination.next')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
